import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { META, generateRefundPolicyRichSnippet } from "./meta";
import { animateTitle, animateContent, cleanupScrollTriggers } from "./gsap";
import { BUSINESS_NAME } from "@/core/constants/business";

const RefundPolicy: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Rich snippet data for SEO
  const richSnippet = generateRefundPolicyRichSnippet();

  useEffect(() => {
    // Apply animations after a small delay to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current && contentRef.current) {
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        animateContent(
          { current: contentRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Clean up animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link
          rel="canonical"
          href={`https://slsdispensary.com${META.canonicalUrl}`}
        />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={META.title} />
        <meta property="og:description" content={META.meta_description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://slsdispensary.com${META.canonicalUrl}`}
        />
        <meta
          property="og:image"
          content="https://slsdispensary.com/images/street-lawyer-services-dc-dispensary-entrance.jpg"
        />
        <meta property="og:site_name" content={BUSINESS_NAME} />

        {/* JSON-LD for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      </Helmet>

      {/* Main Content */}
      <section ref={sectionRef} className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h1
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold mb-8 text-center"
          >
            Refund Policy
          </h1>

          <div
            ref={contentRef}
            className="max-w-3xl mx-auto prose prose-lg refund-policy-content"
          >
            <p className="text-xl text-gray-700 mb-8 refund-policy-intro">
              At {BUSINESS_NAME}, we're committed to providing high-quality
              cannabis products. We understand the importance of customer
              satisfaction and have established this refund policy to ensure
              transparency in our business operations.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Product Quality Guarantee
            </h3>
            <p>
              We take great pride in the quality of our cannabis products. All
              items undergo rigorous quality control measures before they reach
              our shelves. However, if you believe a product is defective or
              does not meet our standards, please contact us within 24 hours of
              purchase.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">Return Policy</h3>
            <p>
              Due to the nature of cannabis products and regulatory
              requirements, we maintain a strict no-return policy once products
              leave our premises. For your safety and in compliance with health
              regulations, we cannot accept returns of cannabis products under
              any circumstances.
            </p>

            <h4 className="text-xl font-bold mt-6 mb-3">Exceptions:</h4>
            <ul>
              <li>
                <strong>Unopened Accessories:</strong> Non-cannabis products
                such as unopened accessories may be eligible for exchange or
                store credit within 7 days of purchase if they're in their
                original packaging with receipt.
              </li>
              <li>
                <strong>Product Defects:</strong> If you purchase a product that
                has a manufacturer's defect, please bring it to our attention
                immediately. We will evaluate each situation on a case-by-case
                basis.
              </li>
            </ul>

            <h3 className="text-2xl font-bold mt-8 mb-4">Exchange Process</h3>
            <p>
              For eligible items, exchanges may be considered under the
              following conditions:
            </p>
            <ol>
              <li>You must present the original receipt</li>
              <li>The item must be in its original, unopened packaging</li>
              <li>
                The exchange must be requested within the specified timeframe
              </li>
              <li>
                Valid government-issued ID matching the original purchaser must
                be presented
              </li>
            </ol>

            <h3 className="text-2xl font-bold mt-8 mb-4">Store Credit</h3>
            <p>
              In certain circumstances where a return cannot be processed but
              the customer experienced a legitimate issue with a product, we may
              offer store credit at our discretion. Store credit is
              non-transferable and has no cash value.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Customer Satisfaction
            </h3>
            <p>
              Your satisfaction is our priority. If you have any concerns about
              a product you've purchased, please speak with one of our
              knowledgeable staff members. We're committed to finding a
              reasonable solution whenever possible.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">
              Modification of Policy
            </h3>
            <p>
              {BUSINESS_NAME} reserves the right to modify this refund policy at
              any time. Changes will be posted in-store and on our website. The
              policy in effect at the time of purchase will apply to that
              transaction.
            </p>

            <h3 className="text-2xl font-bold mt-8 mb-4">Contact Us</h3>
            <p>
              If you have questions about our refund policy or need to discuss a
              specific purchase, please don't hesitate to contact our customer
              service team at our DC location: (202) 340-8277 or by email at
              info@streetlawyerservices.com.
            </p>

            <p className="text-sm text-gray-500 mt-8 italic">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default RefundPolicy;
